import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, Observable, Subscription, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import { CmsArticle } from '@fcom/core-api';
import { finShare } from '@fcom/rx';
import { CmsDataService, ConfigService, WindowRef, unsubscribe } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonTheme,
  IconPosition,
  ModalButtons,
  NotificationButtonStyle,
  NotificationTheme,
  StepperStep,
} from '@fcom/ui-components';
import { LoyaltyRoutesPath } from '@fcom/loyalty-core';
import { TwoFactorAuthService, TwoFactorAuthStatus } from '@fcom/loyalty-profile/services/two-factor-auth.service';
import { GtmService } from '@fcom/common/gtm';
import { GaContext } from '@fcom/common/interfaces/gtm.interface';
import { ElementActions, ElementTypes } from '@fcom/common';

import { AviosPartner } from '../../interfaces/avios-linking-and-exchange.interface';
import { AviosLinkingService, AviosAccountLinkingStatus } from '../../services/avios-linking.service';
import {
  PlusProfileQueryParamSectionValue,
  PlusProfileQueryParams,
} from '../../views/plus-profile/plus-profile.component';

export enum LinkAccountsFlowStep {
  ENABLE_TWO_FACTOR_AUTH = 'enable-two-factor-auth',
  LINK_ACCOUNTS = 'link-accounts',
}

export interface LinkAccountsStepperStep extends StepperStep {
  step: LinkAccountsFlowStep;
}

export enum AviosLinkingAndExchangeModal {
  LinkAccounts = 'linkAccounts',
  TransferAvios = 'transferAvios',
  UnlinkAccount = 'unlinkAccount',
}

@Component({
  selector: 'fin-avios-linking-and-exchange',
  templateUrl: 'avios-linking-and-exchange.component.html',
  styleUrls: ['./avios-linking-and-exchange.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AviosLinkingAndExchangeComponent implements OnInit, OnDestroy {
  readonly LinkAccountsFlowStep = LinkAccountsFlowStep;
  readonly ButtonTheme = ButtonTheme;
  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly AviosPartner = AviosPartner;
  readonly TwoFactorAuthStatus = TwoFactorAuthStatus;
  readonly AviosAccountLinkingStatus = AviosAccountLinkingStatus;
  readonly AviosLinkingAndExchangeModal = AviosLinkingAndExchangeModal;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  aviosLinkingStatus$: Observable<AviosAccountLinkingStatus>;

  transferBetweenAccountsFragment$: Observable<CmsArticle>;

  stepperSteps$: Observable<LinkAccountsStepperStep[]>;
  stepperActiveStep$ = new BehaviorSubject(LinkAccountsFlowStep.ENABLE_TWO_FACTOR_AUTH);

  stepperActiveStepIndex$: Observable<number>;

  test$: Observable<CmsArticle>;

  modalFragments: {
    [key in AviosLinkingAndExchangeModal]: Observable<CmsArticle | null>;
  } = {
    [AviosLinkingAndExchangeModal.LinkAccounts]: null,
    [AviosLinkingAndExchangeModal.TransferAvios]: null,
    [AviosLinkingAndExchangeModal.UnlinkAccount]: null,
  };
  modalOpened: AviosLinkingAndExchangeModal | undefined;

  get isModalOpened(): boolean {
    return !!this.modalOpened;
  }

  set isModalOpened(value: boolean) {
    if (!value) {
      this.modalOpened = undefined;
    }
  }

  private subscriptions = new Subscription();

  constructor(
    public twoFactorAuthService: TwoFactorAuthService,
    private cmsDataService: CmsDataService,
    private languageService: LanguageService,
    private aviosLinkingService: AviosLinkingService,
    private configService: ConfigService,
    private router: Router,
    private windowRef: WindowRef,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.aviosLinkingStatus$ = this.aviosLinkingService.status$;

    this.transferBetweenAccountsFragment$ = this.aviosLinkingStatus$.pipe(
      switchMap((aviosLinkingStatus) => {
        const transferBetweenAccountsFragmentName =
          aviosLinkingStatus === AviosAccountLinkingStatus.ACTIVE
            ? 'transferAviosBetweenAccountsActive'
            : 'transferAviosBetweenAccountsInactive';
        return this.languageService.translate<string>(`LOYALTY.fragments.${transferBetweenAccountsFragmentName}.url`);
      }),
      switchMap((fragmentUrl) => this.cmsDataService.getFragmentJson<CmsArticle>(fragmentUrl)),
      finShare()
    );

    this.stepperSteps$ = of([
      {
        step: LinkAccountsFlowStep.ENABLE_TWO_FACTOR_AUTH,
        title: 'LOYALTY.aviosLinkingAndExchange.enable2faAuthentication.title',
        ariaLabel: 'LOYALTY.aviosLinkingAndExchange.enable2faAuthentication.title',
      },
      {
        step: LinkAccountsFlowStep.LINK_ACCOUNTS,
        title: 'LOYALTY.aviosLinkingAndExchange.linkYourAccounts.title',
        ariaLabel: 'LOYALTY.aviosLinkingAndExchange.linkYourAccounts.title',
      },
    ]);

    this.subscriptions.add(
      this.twoFactorAuthService.enabledStatus$
        .pipe(
          tap(({ isEnabled }) => {
            this.stepperActiveStep$.next(
              isEnabled ? LinkAccountsFlowStep.LINK_ACCOUNTS : LinkAccountsFlowStep.ENABLE_TWO_FACTOR_AUTH
            );
          })
        )
        .subscribe()
    );

    this.stepperActiveStepIndex$ = this.stepperActiveStep$.pipe(
      withLatestFrom(this.stepperSteps$),
      map(([activeStep, steps]) => {
        return steps.findIndex((step) => step.step === activeStep);
      })
    );

    this.modalFragments = {
      [AviosLinkingAndExchangeModal.LinkAccounts]: this.languageService
        .translate('LOYALTY.fragments.aviosLinkingAndExchangeLinkAccountsModal.url')
        .pipe(
          take(1),
          switchMap((fragmentUrl) => this.cmsDataService.getFragmentJson<CmsArticle>(fragmentUrl)),
          finShare()
        ),
      [AviosLinkingAndExchangeModal.TransferAvios]: this.languageService
        .translate('LOYALTY.fragments.aviosLinkingAndExchangeTransferAviosModal.url')
        .pipe(
          take(1),
          switchMap((fragmentUrl) => this.cmsDataService.getFragmentJson<CmsArticle>(fragmentUrl)),
          finShare()
        ),
      [AviosLinkingAndExchangeModal.UnlinkAccount]: this.languageService
        .translate('LOYALTY.fragments.aviosLinkingAndExchangeUnlinkAccountModal.url')
        .pipe(
          take(1),
          switchMap((fragmentUrl) => this.cmsDataService.getFragmentJson<CmsArticle>(fragmentUrl)),
          finShare()
        ),
    };
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onStepperClicked(stepperStep: StepperStep): void {
    const { step } = stepperStep as LinkAccountsStepperStep;
    this.stepperActiveStep$.next(step);
  }

  onEnable2FAAuthenticationClickBtn(isTwoFactorAuthEnabled: boolean): void {
    if (isTwoFactorAuthEnabled) {
      this.stepperActiveStep$.next(LinkAccountsFlowStep.LINK_ACCOUNTS);
    } else {
      this.gtmService.trackElement(
        'avios-linking-and-exchange-enable-2fa-button',
        GaContext.FINNAIR_PLUS,
        ElementTypes.BUTTON,
        undefined,
        ElementActions.CLICK
      );
      this.router.navigate([this.languageService.langValue, LoyaltyRoutesPath.PLUS, LoyaltyRoutesPath.PROFILE], {
        queryParams: {
          [PlusProfileQueryParams.Section]: PlusProfileQueryParamSectionValue.TwoFactorAuth,
          [PlusProfileQueryParams.TwoFactorAuthEnabledBackButtonUrl]: `/${this.languageService.langValue}/${LoyaltyRoutesPath.PLUS}/${LoyaltyRoutesPath.BALANCE_AND_TRANSACTIONS}`,
        },
      });
    }
  }

  onLinkAccountsClickBtn(): void {
    this.modalOpened = AviosLinkingAndExchangeModal.LinkAccounts;
  }

  onTransferAviosClickBtn(): void {
    this.modalOpened = AviosLinkingAndExchangeModal.TransferAvios;
  }

  onUnlinkAccountClickBtn(): void {
    this.modalOpened = AviosLinkingAndExchangeModal.UnlinkAccount;
  }

  onModalConfirm(): void {
    const trackGtm = (elementName: string) => {
      this.gtmService.trackElement(
        `avios-linking-and-exchange-${elementName}`,
        GaContext.FINNAIR_PLUS,
        ElementTypes.BUTTON,
        undefined,
        ElementActions.CLICK
      );
    };

    switch (this.modalOpened) {
      case AviosLinkingAndExchangeModal.LinkAccounts:
        trackGtm('link-accounts');
        break;
      case AviosLinkingAndExchangeModal.TransferAvios:
        trackGtm('transfer-avios');
        break;
      case AviosLinkingAndExchangeModal.UnlinkAccount:
        trackGtm('unlink-account');
        break;
    }

    this.windowRef.nativeWindow.open(this.configService.cfg.britishAirwaysExchangeUrl, '_blank');
  }
}
